import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { permissionApi } from '@/services/permissionsRolesApi';
import { useToast } from '@/hooks/useToast';
import { useAdminAuth } from '@/contexts/AdminAuthContext';
import { Button } from '@/components/ui/Button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card';
import { ArrowLeft, Shield, BarChart3 } from 'lucide-react';
import type { PermissionStatistics } from '@/types/permissionsRoles';

export const PermissionStatsPage: React.FC = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [stats, setStats] = useState<PermissionStatistics | null>(null);
  const { hasPermission } = useAdminAuth();
  const { toast } = useToast();

  const canRead = hasPermission('READ_PERMISSION', 'permission');

  useEffect(() => {
    if (canRead) {
      fetchStats();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- fetchStats is stable, run when canRead changes
  }, [canRead]);

  const fetchStats = async () => {
    try {
      setIsLoading(true);
      const data = await permissionApi.getStatistics();
      setStats(data);
    } catch (error: unknown) {
      console.error('Error fetching stats:', error);
      toast({
        variant: 'destructive',
        title: 'Error',
        description: (error as Error)?.message ?? 'Failed to load statistics',
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (!canRead) {
    return (
      <div className="text-center py-8">
        <p className="text-muted-foreground">You do not have permission to view statistics.</p>
        <Button asChild className="mt-4">
          <Link to="/admin/permissions">Back to List</Link>
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" asChild>
            <Link to="/admin/permissions">
              <ArrowLeft className="h-4 w-4" />
            </Link>
          </Button>
          <div>
            <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
              <BarChart3 className="h-8 w-8" />
              Permission Statistics
            </h1>
            <p className="text-muted-foreground mt-2">Overview of permissions</p>
          </div>
        </div>
      </div>

      {isLoading ? (
        <div className="text-center py-8">Loading statistics...</div>
      ) : !stats ? (
        <div className="text-center py-8 text-muted-foreground">No statistics available</div>
      ) : (
        <>
          {/* Overview Cards */}
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-5">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Total Permissions</CardTitle>
                <Shield className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{stats.total}</div>
                <p className="text-xs text-muted-foreground">All permissions</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Active</CardTitle>
                <Shield className="h-4 w-4 text-green-600" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-green-600">{stats.active}</div>
                <p className="text-xs text-muted-foreground">Currently active</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Inactive</CardTitle>
                <Shield className="h-4 w-4 text-gray-600" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-gray-600">{stats.inactive}</div>
                <p className="text-xs text-muted-foreground">Currently inactive</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">System</CardTitle>
                <Shield className="h-4 w-4 text-blue-600" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-blue-600">{stats.system}</div>
                <p className="text-xs text-muted-foreground">System permissions</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Custom</CardTitle>
                <Shield className="h-4 w-4 text-purple-600" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-purple-600">{stats.custom}</div>
                <p className="text-xs text-muted-foreground">Custom permissions</p>
              </CardContent>
            </Card>
          </div>

          {/* Distribution by Resource */}
          {Object.keys(stats.byResource).length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>Distribution by Resource</CardTitle>
                <CardDescription>Number of permissions per resource</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {Object.entries(stats.byResource)
                    .sort(([, a], [, b]) => b - a)
                    .map(([resource, count]) => (
                      <div key={resource} className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <span className="font-medium">{resource || 'No Resource'}</span>
                        </div>
                        <div className="flex items-center gap-4">
                          <div className="w-32 bg-secondary rounded-full h-2">
                            <div
                              className="bg-primary h-2 rounded-full"
                              style={{
                                width: `${(count / stats.total) * 100}%`,
                              }}
                            />
                          </div>
                          <span className="text-sm font-medium w-12 text-right">{count}</span>
                        </div>
                      </div>
                    ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Distribution by Action */}
          {Object.keys(stats.byAction).length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>Distribution by Action</CardTitle>
                <CardDescription>Number of permissions per action</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {Object.entries(stats.byAction)
                    .sort(([, a], [, b]) => b - a)
                    .map(([action, count]) => (
                      <div key={action} className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <span className="font-medium">{action}</span>
                        </div>
                        <div className="flex items-center gap-4">
                          <div className="w-32 bg-secondary rounded-full h-2">
                            <div
                              className="bg-primary h-2 rounded-full"
                              style={{
                                width: `${(count / stats.total) * 100}%`,
                              }}
                            />
                          </div>
                          <span className="text-sm font-medium w-12 text-right">{count}</span>
                        </div>
                      </div>
                    ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Recent Activity */}
          {stats.recentActivity && stats.recentActivity.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>Recent Activity</CardTitle>
                <CardDescription>Last 10 created/updated permissions</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {stats.recentActivity.map((permission) => (
                    <div
                      key={permission.id}
                      className="flex items-center justify-between p-3 rounded-md border"
                    >
                      <div>
                        <div className="font-medium">{permission.name}</div>
                        <div className="text-sm text-muted-foreground">
                          {new Date(permission.updatedAt).toLocaleString()}
                        </div>
                      </div>
                      <Button variant="ghost" size="sm" asChild>
                        <Link to={`/admin/permissions/${permission.id}`}>View</Link>
                      </Button>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </>
      )}
    </div>
  );
};

export default PermissionStatsPage;
